import axios from 'axios';

function hostnameAgencyLabel(hostname, rootDomain) {
  if (!hostname || !rootDomain) return null;
  const hostnameLower = hostname.toLowerCase();
  const rootDomainLower = rootDomain.toLowerCase();
  if (!hostnameLower.endsWith(rootDomainLower)) return null;
  if (hostnameLower === rootDomainLower) return null;
  const suffixWithDot = '.' + rootDomainLower;
  if (!hostnameLower.endsWith(suffixWithDot)) return null;
  const agencyLabel = hostnameLower.slice(0, -suffixWithDot.length);
  if (!agencyLabel || agencyLabel.includes('.')) return null;
  return agencyLabel;
}

function inferRootDomainFromHostname(hostname) {
  if (!hostname) return '';
  const parts = hostname.split('.').filter(Boolean);
  if (parts.length < 3) return '';
  return parts.slice(1).join('.');
}

function resolveRootDomain(getGlobal) {
  const vueGlobal = typeof getGlobal === 'function' ? getGlobal() : null;
  const fromGlobal = (vueGlobal && vueGlobal.companyrootdomain) || '';
  if (fromGlobal && String(fromGlobal).trim() !== '') {
    return String(fromGlobal).trim();
  }
  return inferRootDomainFromHostname(window.location.hostname);
}

const PLATFORM_LABELS = new Set(['applocal', 'app', 'www']);
/** sessionStorage key when view-as is active (must match store.js and other callers) */
export const VIEW_AS_ACTIVE_SESSION_KEY = 'viewAsActive';

/**
 * Strip stored subdomain to a single DNS label (agency slug), whether DB has "slug" or "slug.rootdomain".
 */
function normalizeAgencySubdomainLabel(storedSubdomain, rootDomain) {
  const storedNormalized = (storedSubdomain || '').toString().trim().toLowerCase();
  const rootDomainNormalized = (rootDomain || '').toString().trim().toLowerCase();
  if (!storedNormalized) return '';
  if (!rootDomainNormalized) {
    return storedNormalized.split('.')[0] || '';
  }
  const dottedRootDomain = '.' + rootDomainNormalized;
  if (storedNormalized.endsWith(dottedRootDomain)) {
    return storedNormalized.slice(0, -dottedRootDomain.length);
  }
  if (storedNormalized.includes('.')) {
    return storedNormalized.split('.')[0] || '';
  }
  return storedNormalized;
}

/**
 * True when the SPA is served from the platform app host (not an agency vanity subdomain).
 * Used so view-as "subdomainAgency" in localStorage does not override domain resolution on applocal/app/www.
 */
export function isPlatformAppHostname(hostname) {
  if (!hostname) return false;
  const hostnameLower = hostname.toLowerCase();
  if (hostnameLower === 'localhost' || hostnameLower === '127.0.0.1') return true;
  const firstLabel = hostnameLower.split('.')[0];
  return PLATFORM_LABELS.has(firstLabel);
}

let redirecting = false;

let inFlightUserFetch = null;

let routeDebounceTimer = null;

async function runAgencySubdomainHostnameCheckImmediate({ store, getGlobal }) {
  if (redirecting) return;

  const hostnameLower = window.location.hostname.toLowerCase();
  // Never bounce root impersonation / platform app tabs to an agency vanity host.
  if (isPlatformAppHostname(hostnameLower)) return;
  if (
    typeof sessionStorage !== 'undefined' &&
    sessionStorage.getItem(VIEW_AS_ACTIVE_SESSION_KEY) === '1'
  ) {
    return;
  }
  const vueGlobal = typeof getGlobal === 'function' ? getGlobal() : null;
  if (vueGlobal && vueGlobal.globalviewmode) return;

  const accessToken = localStorage.getItem('access_token');
  if (!accessToken || accessToken === 'null' || accessToken === 'undefined') return;

  const rootDomain = resolveRootDomain(getGlobal);
  if (!rootDomain) return;

  const currentHostLabel = hostnameAgencyLabel(hostnameLower, rootDomain);
  if (!currentHostLabel) return;

  const userData = store.getters.userData;
  if (!userData || userData.systemuser) return;
  if (!['user', 'userdownline', 'client', 'sales'].includes(userData.user_type)) return;

  axios.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken;

  if (inFlightUserFetch) {
    await inFlightUserFetch.catch(() => {});
  }

  inFlightUserFetch = store.dispatch('retrieveUserData', {}).catch(() => 'failed');
  const retrieveResult = await inFlightUserFetch;
  inFlightUserFetch = null;

  if (retrieveResult !== 'success') return;

  const freshUser = store.getters.userData;
  if (!freshUser) return;

  const canonicalLabel = normalizeAgencySubdomainLabel(freshUser.subdomain, rootDomain);
  if (!canonicalLabel) return;

  if (canonicalLabel === currentHostLabel.toLowerCase()) return;

  redirecting = true;
  const rootDomainLower = rootDomain.toLowerCase();
  const portSegment = window.location.port ? ':' + window.location.port : '';
  const redirectUrl = `${window.location.protocol}//${canonicalLabel}.${rootDomainLower}${portSegment}${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.location.replace(redirectUrl);
}

/**
 * @param {{ store: object, getGlobal: () => object }} deps
 * @param {{ debounceMs?: number }} [options]
 */
export function runAgencySubdomainHostnameCheck(deps, options = {}) {
  const debounceMs = typeof options.debounceMs === 'number' ? options.debounceMs : 0;

  if (debounceMs <= 0) {
    return runAgencySubdomainHostnameCheckImmediate(deps).catch(() => {});
  }

  clearTimeout(routeDebounceTimer);
  routeDebounceTimer = setTimeout(() => {
    routeDebounceTimer = null;
    runAgencySubdomainHostnameCheckImmediate(deps).catch(() => {});
  }, debounceMs);
  return Promise.resolve();
}

/**
 * Same-origin tabs only (same hostname:port). Other browsers need refresh or menu navigation
 * on that origin to pick up changes — they do not share localStorage.
 */
export const AGENCY_HOST_SYNC_STORAGE_KEY = 'emm_agency_host_sync_ping';

let hostSyncDebounceTimer = null;

function scheduleAgencyHostSyncCheck(deps, delayMs) {
  clearTimeout(hostSyncDebounceTimer);
  const delay = typeof delayMs === 'number' ? delayMs : 200;
  hostSyncDebounceTimer = setTimeout(() => {
    hostSyncDebounceTimer = null;
    try {
      if (deps.store && deps.store.getters && deps.store.getters.loggedIn) {
        runAgencySubdomainHostnameCheck(deps, { debounceMs: 0 }).catch(() => {});
      }
    } catch {
      /* ignore */
    }
  }, delay);
}

/**
 * Same-origin sibling tabs only: `storage` fires in other tabs, not the writer.
 * Use after menu navigation so idle tabs pick up canonical subdomain changes.
 */
export function bumpAgencyHostSyncPeersStorage() {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(AGENCY_HOST_SYNC_STORAGE_KEY, String(Date.now()));
    }
  } catch {
    /* ignore */
  }
}

/** After subdomain / white-label save: same-origin sibling tabs react via `storage` (see install). */
export const notifyAgencyHostCanonicalMayHaveChanged = bumpAgencyHostSyncPeersStorage;

/** Listens for sibling-tab pings (same origin). Does not use visibilitychange. */
export function installAgencyHostCanonicalSync(deps) {
  if (typeof window === 'undefined') return;
  if (window.__emmAgencyHostCanonicalSyncInstalled) return;
  window.__emmAgencyHostCanonicalSyncInstalled = true;

  const scheduleSyncAfterPeerPing = () => scheduleAgencyHostSyncCheck(deps, 150);

  window.addEventListener('storage', (storageEvent) => {
    if (storageEvent.key === AGENCY_HOST_SYNC_STORAGE_KEY && storageEvent.newValue != null) {
      scheduleSyncAfterPeerPing();
    }
  });
}
